/**
 * PlotExtractionPanel — Dockable panel for viewing and editing the
 * AI-inferred plot structure (logline, structure beats, protagonist arc,
 * themes, subplots). Syncs with PlotBuilderDialog via shared MongoDB storage.
 *
 * Tabs:
 *   Overview   — Logline, conflict, antagonist, sales readiness, themes
 *   Beats      — Editable structure beats (chapter + description)
 *   Protagonist — Goal / Need / Flaw / Transformation
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BookMarked, Zap, Loader2, Save, ChevronDown, ChevronUp,
  Sparkles, Target, BookOpenText, TrendingUp, ExternalLink,
} from 'lucide-react';
import { PanelFrame } from '../workspace/PanelFrame';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { usePW } from '@/lib/store';
import { plotExtractionApi } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────────────
//  Structure definitions (mirrors backend)
// ─────────────────────────────────────────────────────────────────────
const STRUCTURES = {
  '3-act': {
    label: 'Classic 3-Act',
    beats: [
      { key: 'inciting_incident', label: 'Inciting Incident',          hint: 'The event that pulls the hero into the conflict.' },
      { key: 'first_plot_point',  label: 'First Plot Point / Lock-In', hint: 'End of Act 1 — the hero is fully committed.' },
      { key: 'midpoint',          label: 'Midpoint Shift',             hint: 'Halfway: hero pivots from reactive to proactive.' },
      { key: 'all_is_lost',       label: 'All Is Lost',                hint: 'End of Act 2 — darkest moment before the climax.' },
      { key: 'climax',            label: 'Climax',                     hint: 'Final confrontation / peak dramatic tension.' },
      { key: 'resolution',        label: 'Resolution',                 hint: 'Aftermath and new status quo.' },
    ],
  },
  '5-act': {
    label: '5-Act Pyramid',
    beats: [
      { key: 'exposition',      label: 'Exposition',         hint: 'World and characters established.' },
      { key: 'rising_action',   label: 'Rising Action',      hint: 'Complications escalate.' },
      { key: 'climax_midpoint', label: 'Climax (Midpoint)',  hint: 'Peak tension — point of no return.' },
      { key: 'falling_action',  label: 'Falling Action',     hint: 'Consequences unfold.' },
      { key: 'denouement',      label: 'Dénouement',        hint: 'Resolution and new normal.' },
    ],
  },
  '7-act-dan-wells': {
    label: 'Dan Wells 7-Point',
    beats: [
      { key: 'hook',        label: '1. Hook',        hint: "Protagonist's want vs need." },
      { key: 'plot_turn_1', label: '2. Plot Turn 1', hint: 'Inciting event.' },
      { key: 'pinch_1',     label: '3. Pinch 1',     hint: 'First major obstacle.' },
      { key: 'midpoint',    label: '4. Midpoint',    hint: 'Hero turns proactive.' },
      { key: 'pinch_2',     label: '5. Pinch 2',     hint: 'All-is-lost moment.' },
      { key: 'plot_turn_2', label: '6. Plot Turn 2', hint: 'Discovery of what is needed.' },
      { key: 'resolution',  label: '7. Resolution',  hint: 'Climax and new status quo.' },
    ],
  },
  '6-element': {
    label: '6-Element',
    beats: [
      { key: 'exposition',        label: 'Exposition',         hint: 'Status quo established.' },
      { key: 'inciting_incident', label: 'Inciting Incident',  hint: 'Catalyst that ignites the conflict.' },
      { key: 'rising_action',     label: 'Rising Action',      hint: 'Escalating obstacles.' },
      { key: 'climax',            label: 'Climax',             hint: 'Peak tension.' },
      { key: 'falling_action',    label: 'Falling Action',     hint: 'Tension eases.' },
      { key: 'resolution',        label: 'Resolution',         hint: 'New status quo.' },
    ],
  },
};

function normaliseBeats(storedBeats, structureType) {
  const defs = (STRUCTURES[structureType] || STRUCTURES['3-act']).beats;
  if (!storedBeats?.length) {
    return defs.map(d => ({ key: d.key, label: d.label, hint: d.hint, chapter: '', description: '' }));
  }
  const byKey = Object.fromEntries(storedBeats.map(b => [b.key, b]));
  return defs.map(d => ({
    key:         d.key,
    label:       d.label,
    hint:        d.hint,
    chapter:     (byKey[d.key]?.chapter ?? ''),
    description: (byKey[d.key]?.description ?? ''),
  }));
}

function emptyPlot(structureType = '3-act') {
  return {
    logline:          '',
    central_conflict: '',
    antagonist:       '',
    protagonist:      { goal: '', need: '', flaw: '', transformation: '' },
    structure_type:   structureType,
    structure_beats:  normaliseBeats([], structureType),
    subplots:         ['', '', '', '', ''],
    themes:           ['', '', '', '', ''],
    sales_readiness_score: null,
  };
}

function normalisePlot(pe) {
  if (!pe) return emptyPlot();
  const sType = pe.structure_type || '3-act';
  const subplots = [...(pe.subplots || [])];
  while (subplots.length < 5) subplots.push('');
  const themes = [...(pe.themes || [])];
  while (themes.length < 5) themes.push('');
  return {
    ...emptyPlot(sType),
    ...pe,
    protagonist:     { ...emptyPlot().protagonist, ...(pe.protagonist || {}) },
    structure_beats: normaliseBeats(pe.structure_beats, sType),
    subplots,
    themes,
  };
}

// ─────────────────────────────────────────────────────────────────────
//  Score ring (compact)
// ─────────────────────────────────────────────────────────────────────
function MiniScoreRing({ score }) {
  if (score == null) return null;
  const pct   = Math.min(100, Math.max(0, score));
  const r     = 18;
  const circ  = 2 * Math.PI * r;
  const dash  = (pct / 100) * circ;
  const color = pct >= 85 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" data-testid="pe-score-ring">
      <svg width="46" height="46" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="23" cy="23" r={r} fill="none" stroke="currentColor" strokeOpacity=".12" strokeWidth="3.5" />
        <circle cx="23" cy="23" r={r} fill="none" stroke={color} strokeWidth="3.5"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      </svg>
      <span className="absolute text-[11px] font-bold" style={{ color }}>{pct}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Tab bar
// ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',    icon: BookOpenText, label: 'Overview' },
  { id: 'beats',       icon: TrendingUp,   label: 'Structure' },
  { id: 'protagonist', icon: Target,       label: 'Protagonist' },
];

function TabBar({ active, onChange }) {
  return (
    <div className="flex shrink-0 gap-0 overflow-x-auto border-b border-panel-border">
      {TABS.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          data-testid={`pe-tab-${id}`}
          onClick={() => onChange(id)}
          className={cn(
            'flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
            active === id
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Empty state
// ─────────────────────────────────────────────────────────────────────
function EmptyState({ extracting, onExtract }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
      <BookMarked className="h-10 w-10 text-muted-foreground/40" />
      <div>
        <p className="text-[13px] font-semibold">No plot structure yet</p>
        <p className="mt-1 max-w-[260px] text-[13px] leading-snug text-muted-foreground">
          Run AI extraction to identify your logline, turning points, protagonist arc, and themes from your manuscript.
        </p>
      </div>
      <Button
        size="sm"
        className="gap-1.5"
        onClick={onExtract}
        disabled={extracting}
        data-testid="pe-extract-empty-btn"
      >
        {extracting
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Sparkles className="h-3.5 w-3.5" />}
        {extracting ? 'Extracting…' : 'Extract from Manuscript'}
      </Button>
      <button
        className="text-[12px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
        onClick={() => window.dispatchEvent(new CustomEvent('loom:open-plot-builder'))}
      >
        Or open the full Plot Builder
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Tabs
// ─────────────────────────────────────────────────────────────────────
function OverviewTab({ draft, onChange }) {
  const themes   = (draft.themes   || []).filter(Boolean);
  const subplots = (draft.subplots || []).filter(Boolean);
  const score    = draft.sales_readiness_score;
  const scoreLabel = score == null ? '' : score >= 85 ? 'Market ready' : score >= 60 ? 'Needs polish' : 'Needs work';
  const scoreColor = score == null ? '' : score >= 85 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-rose-500';

  return (
    <div className="flex-1 overflow-y-auto space-y-4 p-3">
      {/* Sales Readiness */}
      {score != null && (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2.5" data-testid="pe-readiness-row">
          <MiniScoreRing score={score} />
          <div>
            <p className="text-[12px] text-muted-foreground uppercase tracking-wider">Sales Readiness</p>
            <p className={cn('text-[13px] font-semibold', scoreColor)}>{scoreLabel}</p>
          </div>
          <button
            className="ml-auto flex items-center gap-1 text-[11px] text-primary hover:underline"
            onClick={() => window.dispatchEvent(new CustomEvent('loom:open-plot-builder'))}
            data-testid="pe-open-full-dialog-btn"
          >
            Full view <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Logline */}
      <div className="space-y-1">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Logline</label>
        <Textarea
          value={draft.logline || ''}
          onChange={e => onChange('logline', e.target.value)}
          rows={2}
          placeholder="One to two sentence premise…"
          className="resize-y text-[13px] min-h-[52px]"
          data-testid="pe-logline-input"
        />
      </div>

      {/* Central conflict */}
      <div className="space-y-1">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Central Conflict</label>
        <Textarea
          value={draft.central_conflict || ''}
          onChange={e => onChange('central_conflict', e.target.value)}
          rows={2}
          placeholder="The core opposition driving the story…"
          className="resize-y text-[13px] min-h-[52px]"
          data-testid="pe-conflict-input"
        />
      </div>

      {/* Antagonist */}
      <div className="space-y-1">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Antagonist</label>
        <Input
          value={draft.antagonist || ''}
          onChange={e => onChange('antagonist', e.target.value)}
          placeholder="Person, system, or force of opposition…"
          className="text-[13px]"
          data-testid="pe-antagonist-input"
        />
      </div>

      {/* Themes */}
      {themes.length > 0 && (
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Themes</label>
          <div className="flex flex-wrap gap-1.5">
            {themes.map((t, i) => (
              <span key={i} className="rounded-full border border-primary/30 bg-primary/8 px-2 py-0.5 text-[11px] text-primary font-medium">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Subplots */}
      {subplots.length > 0 && (
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Subplots</label>
          <ul className="space-y-1">
            {subplots.map((sp, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[12px] text-muted-foreground">
                <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
                {sp}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BeatsTab({ draft, onChange }) {
  const [openKeys, setOpenKeys] = useState({});
  const toggleKey = (k) => setOpenKeys(prev => ({ ...prev, [k]: !prev[k] }));

  const handleStructureType = (val) => {
    const newBeats = normaliseBeats(draft.structure_beats, val);
    onChange('structure_type', val);
    onChange('structure_beats', newBeats);
  };

  const updateBeat = (idx, field, val) => {
    const beats = [...(draft.structure_beats || [])];
    beats[idx] = { ...beats[idx], [field]: val };
    onChange('structure_beats', beats);
  };

  const beats = draft.structure_beats || [];

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-3">
      {/* Structure type selector */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">Structure</span>
        <Select value={draft.structure_type || '3-act'} onValueChange={handleStructureType}>
          <SelectTrigger className="h-7 text-[12px] flex-1" data-testid="pe-structure-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STRUCTURES).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-[12px]">{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Beats list */}
      {beats.map((beat, idx) => {
        const isOpen = !!openKeys[beat.key];
        const hasCh = beat.chapter !== '' && beat.chapter != null;
        const hasDesc = !!beat.description;
        return (
          <div key={beat.key || idx} className="rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => toggleKey(beat.key)}
              data-testid={`pe-beat-toggle-${beat.key}`}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/30 transition-colors"
            >
              <span className="flex-1 min-w-0">
                <span className="text-[12.5px] font-semibold">{beat.label}</span>
                {hasCh && (
                  <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-600">Ch {beat.chapter}</span>
                )}
                {!hasCh && !hasDesc && (
                  <span className="ml-2 text-[10px] text-muted-foreground/60 italic">empty</span>
                )}
              </span>
              {isOpen
                ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            </button>
            {isOpen && (
              <div className="border-t border-border p-3 space-y-2 bg-muted/10">
                <p className="text-[11px] text-muted-foreground/70 italic">{beat.hint}</p>
                <div className="flex items-center gap-2">
                  <label className="text-[11px] text-muted-foreground shrink-0 w-14">Chapter</label>
                  <Input
                    type="number"
                    min={1}
                    value={beat.chapter ?? ''}
                    onChange={e => updateBeat(idx, 'chapter', e.target.value)}
                    placeholder="—"
                    className="h-7 text-[12px] w-20"
                    data-testid={`pe-beat-ch-${beat.key}`}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] text-muted-foreground">Description</label>
                  <Textarea
                    value={beat.description || ''}
                    onChange={e => updateBeat(idx, 'description', e.target.value)}
                    rows={2}
                    placeholder={beat.hint}
                    className="resize-y text-[12px] min-h-[48px]"
                    data-testid={`pe-beat-desc-${beat.key}`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProtagonistTab({ draft, onChange }) {
  const prot = draft.protagonist || {};

  const fields = [
    { key: 'goal',           label: 'External Goal',       hint: 'What they actively want to achieve.' },
    { key: 'need',           label: 'Internal Need',        hint: 'What they truly need (thematic).' },
    { key: 'flaw',           label: 'Wound / Flaw',         hint: 'The belief or wound holding them back.' },
    { key: 'transformation', label: 'Transformation',       hint: 'How they change by the end.' },
  ];

  const updateProt = (field, val) => {
    onChange('protagonist', { ...prot, [field]: val });
  };

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-4">
      {fields.map(({ key, label, hint }) => (
        <div key={key} className="space-y-1" data-testid={`pe-prot-${key}`}>
          <div className="flex items-baseline justify-between">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
          </div>
          <p className="text-[11px] text-muted-foreground/70 italic">{hint}</p>
          <Textarea
            value={prot[key] || ''}
            onChange={e => updateProt(key, e.target.value)}
            rows={2}
            className="resize-y text-[13px] min-h-[52px]"
            data-testid={`pe-prot-input-${key}`}
          />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Main panel
// ─────────────────────────────────────────────────────────────────────
export function PlotExtractionPanel({ onClose, maximized, onMaximize }) {
  const { project, setProject } = usePW();
  const pid = project?.id;

  const [tab,        setTab]        = useState('overview');
  const [draft,      setDraft]      = useState(() => normalisePlot(project?.plot_extraction));
  const [dirty,      setDirty]      = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving,     setSaving]     = useState(false);

  // Sync when project.plot_extraction changes externally (e.g. PlotBuilderDialog saves)
  useEffect(() => {
    setDraft(normalisePlot(project?.plot_extraction));
    setDirty(false);
  }, [project?.plot_extraction]);

  const hasPlot = !!(project?.plot_extraction?.logline || project?.plot_extraction?.structure_beats?.length);

  const handleChange = useCallback((field, val) => {
    setDraft(prev => ({ ...prev, [field]: val }));
    setDirty(true);
  }, []);

  const handleExtract = useCallback(async () => {
    if (!pid) return;
    setExtracting(true);
    try {
      const res = await plotExtractionApi.extract(pid, draft.structure_type || '3-act');
      const pe  = res.plot_extraction;
      setDraft(normalisePlot(pe));
      setDirty(false);
      setProject(prev => ({ ...prev, plot_extraction: pe }));
      toast.success('Plot structure extracted.', {
        description: 'Switch to Arc Visualizer → Story Map to see characters on your acts.',
        action: {
          label: 'Story Map →',
          onClick: () => {
            window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'arc' }));
            setTimeout(() => window.dispatchEvent(new CustomEvent('loom:arc-switch-tab', { detail: 'storymap' })), 150);
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Extraction failed — make sure you have drafted chapters.');
    } finally {
      setExtracting(false);
    }
  }, [pid, draft.structure_type, setProject]);

  const handleSave = useCallback(async () => {
    if (!pid || !dirty) return;
    setSaving(true);
    try {
      const res = await plotExtractionApi.update(pid, {
        logline:          draft.logline,
        central_conflict: draft.central_conflict,
        antagonist:       draft.antagonist,
        protagonist:      draft.protagonist,
        structure_type:   draft.structure_type,
        structure_beats:  draft.structure_beats,
        themes:           (draft.themes   || []).filter(Boolean),
        subplots:         (draft.subplots || []).filter(Boolean),
      });
      const pe = res.plot_extraction;
      setDraft(normalisePlot(pe));
      setDirty(false);
      setProject(prev => ({ ...prev, plot_extraction: pe }));
      toast.success('Plot saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [pid, dirty, draft, setProject]);

  const scoreLabel = useMemo(() => {
    const c = project?.plot_extraction?.structure_beats?.filter(b => b.chapter > 0)?.length || 0;
    return `${c} beat${c === 1 ? '' : 's'}`;
  }, [project?.plot_extraction]);

  return (
    <PanelFrame
      title="Plot Blueprint"
      icon={BookMarked}
      onClose={onClose}
      onMaximize={onMaximize}
      maximized={maximized}
      testId="plot-extraction-panel"
      badge={scoreLabel}
    >
      <div className="flex h-full flex-col">
        {/* Action bar */}
        <div className="flex shrink-0 items-center gap-1.5 border-b border-panel-border bg-panel-header/40 px-2 py-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 px-2 text-[12px]"
            onClick={handleExtract}
            disabled={extracting}
            data-testid="pe-extract-btn"
          >
            {extracting
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Sparkles className="h-3 w-3" />}
            {extracting ? 'Extracting…' : 'Extract'}
          </Button>
          <Button
            size="sm"
            variant={dirty ? 'default' : 'outline'}
            className="h-7 gap-1.5 px-2 text-[12px]"
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="pe-save-btn"
          >
            {saving
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Save className="h-3 w-3" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <button
            className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => window.dispatchEvent(new CustomEvent('loom:open-plot-builder'))}
            data-testid="pe-open-builder-link"
            title="Open full Plot Builder"
          >
            Full editor <ExternalLink className="h-3 w-3" />
          </button>
        </div>

        {!hasPlot ? (
          <EmptyState extracting={extracting} onExtract={handleExtract} />
        ) : (
          <>
            <TabBar active={tab} onChange={setTab} />
            {tab === 'overview'    && <OverviewTab    draft={draft} onChange={handleChange} />}
            {tab === 'beats'       && <BeatsTab       draft={draft} onChange={handleChange} />}
            {tab === 'protagonist' && <ProtagonistTab draft={draft} onChange={handleChange} />}
          </>
        )}
      </div>
    </PanelFrame>
  );
}
